import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import CualiaAdvice from './CualiaAdvice';

const FLAG_EMOJI = { VE: '🇻🇪', PA: '🇵🇦', CO: '🇨🇴', BR: '🇧🇷', MX: '🇲🇽', CL: '🇨🇱', AR: '🇦🇷', PE: '🇵🇪' };

const KYCComponent = ({ user }) => {
  const [tab, setTab] = useState('profile');
  const [kycProfile, setKycProfile] = useState(null);
  const [limits, setLimits] = useState(null);
  const [countryConfig, setCountryConfig] = useState(null);
  const [countries, setCountries] = useState({});
  const [loading, setLoading] = useState(true);
  const [docFile, setDocFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [adminSubmissions, setAdminSubmissions] = useState([]);
  const [showCualiaForKyc, setShowCualiaForKyc] = useState(false);

  const isAdmin = user?.email === 'admin@cualapp.com';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [profileRes, countriesRes] = await Promise.all([
        axios.get(`${API}/kyc/my-profile`),
        axios.get(`${API}/kyc/countries`)
      ]);
      setKycProfile(profileRes.data.kyc);
      setLimits(profileRes.data.limits);
      setCountryConfig(profileRes.data.country_config);
      setCountries(countriesRes.data.countries || {});
      if (isAdmin) {
        const subs = await axios.get(`${API}/kyc/admin/submissions`);
        setAdminSubmissions(subs.data.submissions || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const setCountry = async (code) => {
    try {
      await axios.post(`${API}/kyc/set-country`, { country: code });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const submitDocs = async () => {
    if (!docFile || !selfieFile) { alert('Selecciona foto del documento y selfie'); return; }
    // CUALia advierte sobre la sensibilidad de subir documentos personales antes de enviar
    if (!showCualiaForKyc) {
      setShowCualiaForKyc(true);
      return;
    }
    setShowCualiaForKyc(false);
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const formData = new FormData();
      formData.append('document', docFile);
      formData.append('selfie', selfieFile);
      const res = await axios.post(`${API}/kyc/submit-documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitResult(res.data);
      fetchData();
    } catch (err) { setSubmitResult({ status: 'error', message: err.response?.data?.detail || 'Error al enviar documentos' }); }
    finally { setSubmitting(false); }
  };

  const adminAction = async (id, action) => {
    try {
      await axios.post(`${API}/kyc/admin/${action}/${id}`, { level: 'basic', reason: 'Documentos no cumplen requisitos' });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Cargando...</div>;

  const level = kycProfile?.kyc_level || 'unverified';
  const levelColor = level === 'premium' ? 'text-amber-600 bg-amber-50 border-amber-200' : level === 'basic' ? 'text-green-600 bg-green-50 border-green-200' : 'text-gray-600 bg-gray-50 border-gray-200';

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="kyc-module">
      {showCualiaForKyc && (
        <CualiaAdvice
          contextType="kyc_upload"
          metadata={{ country: kycProfile?.country, has_document: !!docFile, has_selfie: !!selfieFile }}
          onAcknowledge={submitDocs}
          onCancel={() => setShowCualiaForKyc(false)}
        />
      )}
      {/* Level badge + Country */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-lg text-sm font-bold border ${levelColor}`} data-testid="kyc-level">
              {limits?.label || 'Sin Verificar'}
            </span>
            <span className="text-sm text-gray-500">Nivel KYC</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Jurisdiccion:</span>
            <div className="flex gap-1">
              {Object.entries(countries).map(([code, cfg]) => (
                <button key={code} onClick={() => setCountry(code)} data-testid={`country-${code}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    kycProfile?.country === code ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                  }`}>
                  {code}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Limits info */}
        {limits && (
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-gray-900">{limits.daily_tx === -1 ? 'Sin limite' : limits.daily_tx}</p>
              <p className="text-xs text-gray-500">Transacciones/dia</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-gray-900">{limits.max_amount === -1 ? 'Sin limite' : `${limits.max_amount} Cc`}</p>
              <p className="text-xs text-gray-500">Monto max/tx</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className={`text-lg font-bold ${limits.can_marketplace ? 'text-green-600' : 'text-red-500'}`}>{limits.can_marketplace ? 'Si' : 'No'}</p>
              <p className="text-xs text-gray-500">Marketplace</p>
            </div>
          </div>
        )}

        {/* Country config */}
        {countryConfig && (
          <div className="mt-4 bg-gray-50 rounded-lg p-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div><span className="text-gray-400">Moneda:</span> <span className="font-medium">{countryConfig.currency_symbol} ({countryConfig.currency_fiat})</span></div>
              <div><span className="text-gray-400">ID:</span> <span className="font-medium">{countryConfig.id_name}</span></div>
              <div><span className="text-gray-400">Formato:</span> <span className="font-mono">{countryConfig.id_format}</span></div>
              <div><span className="text-gray-400">Pago Movil:</span> <span className="font-medium">{countryConfig.pago_movil ? 'Disponible' : 'No disponible'}</span></div>
              {countryConfig.pix && <div><span className="text-gray-400">PIX:</span> <span className="font-medium text-green-600">Disponible</span></div>}
              {countryConfig.spei && <div><span className="text-gray-400">SPEI:</span> <span className="font-medium text-green-600">Disponible</span></div>}
              {countryConfig.transferencia_inmediata && <div><span className="text-gray-400">Transferencia:</span> <span className="font-medium text-green-600">Inmediata</span></div>}
              {countryConfig.yape && <div><span className="text-gray-400">Yape/Plin:</span> <span className="font-medium text-green-600">Disponible</span></div>}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setTab('profile')} data-testid="tab-profile"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'profile' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
          Verificacion
        </button>
        {isAdmin && (
          <button onClick={() => setTab('admin')} data-testid="tab-admin"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'admin' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
            Solicitudes ({adminSubmissions.length})
          </button>
        )}
      </div>

      {/* Verification tab */}
      {tab === 'profile' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {level === 'unverified' ? (
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Verificar Identidad</h3>
              <p className="text-sm text-gray-500">Sube una foto de tu documento de identidad y una selfie para verificar tu cuenta y desbloquear limites mas altos.</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Foto del documento ({countryConfig?.id_name || 'Cedula'})</label>
                <input type="file" accept="image/*" onChange={(e) => setDocFile(e.target.files[0])} data-testid="doc-upload"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                {docFile && <p className="text-xs text-green-600 mt-1">{docFile.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Selfie (foto de tu rostro)</label>
                <input type="file" accept="image/*" onChange={(e) => setSelfieFile(e.target.files[0])} data-testid="selfie-upload"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                {selfieFile && <p className="text-xs text-green-600 mt-1">{selfieFile.name}</p>}
              </div>

              <button onClick={submitDocs} disabled={submitting || !docFile || !selfieFile} data-testid="submit-kyc-btn"
                className="w-full bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-50 font-medium">
                {submitting ? 'Verificando con IA...' : 'Enviar para Verificacion'}
              </button>

              {submitResult && (
                <div data-testid="submit-result" className={`rounded-lg p-4 text-sm ${
                  submitResult.status === 'approved' ? 'bg-green-50 border border-green-200 text-green-800' :
                  submitResult.status === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
                  'bg-amber-50 border border-amber-200 text-amber-800'
                }`}>
                  <p className="font-semibold">{submitResult.message}</p>
                  {submitResult.verification && (
                    <div className="mt-2 space-y-1 text-xs">
                      <p>Documento legible: {submitResult.verification.document_legible ? 'Si' : 'No'}</p>
                      <p>Coincidencia facial: {submitResult.verification.face_match ? 'Si' : 'No'} ({submitResult.verification.confidence})</p>
                      {submitResult.verification.issues?.length > 0 && (
                        <p>Problemas: {submitResult.verification.issues.join(', ')}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 text-center space-y-2">
              <div className="text-4xl">✓</div>
              <h3 className="text-lg font-semibold text-gray-900">Cuenta Verificada</h3>
              <p className="text-sm text-gray-500">Tu nivel KYC es: <strong>{limits?.label}</strong></p>
              {kycProfile?.verified_name && <p className="text-xs text-gray-400">Nombre verificado: {kycProfile.verified_name}</p>}
              {kycProfile?.verified_at && <p className="text-xs text-gray-400">Verificado: {new Date(kycProfile.verified_at).toLocaleDateString('es-VE')}</p>}
            </div>
          )}
        </div>
      )}

      {/* Admin tab */}
      {tab === 'admin' && isAdmin && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Solicitudes KYC Pendientes</h3>
          </div>
          {adminSubmissions.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">Sin solicitudes pendientes</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {adminSubmissions.map(sub => (
                <div key={sub.id} className="px-6 py-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{sub.user_id}</p>
                    <p className="text-xs text-gray-400">{new Date(sub.submitted_at).toLocaleString('es-VE')}</p>
                    {sub.ai_result && (
                      <p className="text-xs text-gray-500 mt-1">
                        IA: {sub.ai_result.recommendation} | Match: {sub.ai_result.face_match ? 'Si' : 'No'} ({sub.ai_result.face_match_confidence})
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => adminAction(sub.id, 'approve')} data-testid={`approve-${sub.id}`}
                      className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium hover:bg-green-200">Aprobar</button>
                    <button onClick={() => adminAction(sub.id, 'reject')} data-testid={`reject-${sub.id}`}
                      className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200">Rechazar</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KYCComponent;
